import React, { useMemo, useState, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import TanStackTable from '../../../../../../shared/design-system/ui/tanstack-table';
import { getCallLogsColumnDefinitions } from './column-definitions';
import { CallLog, GetCallLogsListPayload } from '../../../../types/call-logs';
import { PaginationOptionsChangePayload } from '../../../../../../shared/design-system/ui/tanstack-table/types';
import './call-logs-table.scss';
import Banner, {
  BannerVariant,
} from '../../../../../../shared/design-system/components/atoms/banner';
import { MissedCallIcon } from '../../../../../../shared/svg/missed-call';
import { CallManagementState } from '../../../../../../shared/components/dialer/types';
import ConfirmationModalV3 from '../../../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { ConfirmationModalIconType } from '../../../../../../shared/design-system/components/atoms/confirmation-modal/enum';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { SortOrder } from '../../../../../../shared/enums/order';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';

interface IProps {
  data: CallLog[];
  isLoading?: boolean;
  tabType: GetCallLogsListPayload['type'];
  page: number;
  limit: number;
  totalRows: number;
  onPaginationChange?: (payload: PaginationOptionsChangePayload) => void;
  /** Allow resizing column width by dragging header edges. Set to false to disable. */
  enableColumnResizing?: boolean;
  /** Allow reordering columns by drag-and-drop. Set to false to disable. */
  enableColumnOrdering?: boolean;
  /** Allow pinning (freeze) columns. Set to false to disable. */
  enableColumnPinning?: boolean;
  currentCallManagementState: CallManagementState;
  role: string;
  onDeleteCallLog?: (row: CallLog) => void;
  sendDeleteCallLogRequest?: (callId: number) => void;
  missedCallCount: number;
  handleMarkCallAsSeen: () => void;
  activeCallLogsTab?: string;
  sendAssignCallOutcomeRequest?: (
    outcomeId: number,
    dialerCallLogId: number,
  ) => void;
  setSelectedTemplateId?: (id: number | null) => void;
  selectedTemplateId?: number | null;
  onProspectNameClick?: (ctx: { columnId: number; row: CallLog }) => void;
  onMarkSingleCallAsSeen?: (callId: number) => void;
  sort?: { sortBy: string; order: SortOrder } | null;
  onSort?: (sortBy: string, order: SortOrder) => void;
  isDemoAccount?: boolean;
}

const CallLogsTable: React.FC<IProps> = ({
  data = [],
  isLoading = false,
  tabType,
  page,
  limit,
  totalRows,
  onPaginationChange,
  currentCallManagementState,
  role,
  sendDeleteCallLogRequest,
  missedCallCount,
  handleMarkCallAsSeen,
  activeCallLogsTab,
  sendAssignCallOutcomeRequest,
  setSelectedTemplateId,
  selectedTemplateId,
  onProspectNameClick,
  onMarkSingleCallAsSeen,
  sort,
  onSort,
  isDemoAccount,
}) => {
  const [openVoicemailId, setOpenVoicemailId] = useState<number | null>(null);
  const [callLogToDelete, setCallLogToDelete] = useState<CallLog | null>(null);
  const defaultPinnedColumns = useMemo(() => ['prospectName'], []);
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const pinnedColumns = useMemo(() => ['prospectName'], []);

  const handleDeleteClick = useCallback(
    (row: CallLog) => {
      if (isDemoAccount) {
        setShowGlobalRestrictionModal(true);
        return;
      }
      setCallLogToDelete(row);
    },
    [isDemoAccount],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setCallLogToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (callLogToDelete) {
      sendDeleteCallLogRequest(callLogToDelete.id);
      setCallLogToDelete(null);
    }
  }, [callLogToDelete, sendDeleteCallLogRequest]);

  const baseColumns = useMemo(
    () =>
      getCallLogsColumnDefinitions({
        tabType,
        openVoicemailId,
        setOpenVoicemailId,
        currentCallManagementState,
        role,
        onDeleteClick: handleDeleteClick,
        activeCallLogsTab,
        sendAssignCallOutcomeRequest,
        setSelectedTemplateId,
        selectedTemplateId,
        onProspectNameClick,
        onMarkSingleCallAsSeen,
        sort,
        onSort,
      }),
    [
      tabType,
      openVoicemailId,
      setOpenVoicemailId,
      currentCallManagementState,
      role,
      handleDeleteClick,
      activeCallLogsTab,
      sendAssignCallOutcomeRequest,
      setSelectedTemplateId,
      selectedTemplateId,
      onProspectNameClick,
      onMarkSingleCallAsSeen,
      sort,
      onSort,
    ],
  );

  const defaultColumnOrder = useMemo(
    () => baseColumns?.map((c) => c?.id as string),
    [baseColumns],
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [prevTabType, setPrevTabType] = useState(tabType);

  if (prevTabType !== tabType) {
    setPrevTabType(tabType);
    setColumnOrder(defaultColumnOrder);
  }

  const rightPinnedColumns = useMemo(() => {
    const hasDeleteColumn = baseColumns.some((c) => c.id === 'delete');
    return hasDeleteColumn ? ['delete'] : [];
  }, [baseColumns]);

  const columns = useMemo(() => {
    const idToCol = new Map<string, ColumnDef<CallLog, any>>(
      baseColumns.map(
        (c) => [c.id as string, c as ColumnDef<CallLog, any>] as const,
      ),
    );
    const ordered: ColumnDef<CallLog, any>[] = [];
    columnOrder.forEach((id) => {
      const col = idToCol.get(id);
      if (col) ordered.push(col);
    });
    baseColumns.forEach((col) => {
      const id = col.id as string;
      if (!columnOrder.includes(id)) {
        ordered.push(col as ColumnDef<CallLog, any>);
      }
    });
    return ordered;
  }, [baseColumns, columnOrder]);

  const showPagination = totalRows > 25;

  const paginationOptions = useMemo(
    () =>
      showPagination
        ? {
            page,
            limit,
            totalRows,
            onPaginationOptionsChange: onPaginationChange,
          }
        : undefined,
    [showPagination, page, limit, totalRows, onPaginationChange],
  );

  const paginationSizeOptions = useMemo(() => [25, 50, 75, 100], []);

  return (
    <div className="call-logs-table">
      <TanStackTable<CallLog>
        columns={columns ?? []}
        rows={isLoading ? [] : data}
        isLoading={isLoading}
        paginationOptions={paginationOptions}
        paginationSizeOptions={paginationSizeOptions}
        rightPinnedColumns={rightPinnedColumns}
        defaultPinnedColumns={defaultPinnedColumns}
        enableSorting={true}
        enableRowSelection={false}
        itemName="call"
        tableClassName={`call-logs-tanstack-table ${
          hasPermission(Permissions.PHONE_CALL_DELETE) && !isLoading
            ? 'call-logs-tanstack-table--admin'
            : ''
        } ${
          !isLoading && missedCallCount > 0
            ? 'call-logs-tanstack-table--missed-call-count'
            : ''
        } ${!showPagination ? 'call-logs-tanstack-table--no-pagination' : ''}
         ${isLoading ? 'call-logs-tanstack-table--loading' : ''}
        `}
        showBanner={true}
        pinnedColumns={pinnedColumns}
        enableColumnResizing={false}
        Banner={
          !isLoading &&
          missedCallCount > 0 && (
            <Banner
              pageHeaderBannerMessage={`You have ${missedCallCount} new missed call${
                missedCallCount > 1 ? 's' : ''
              }.`}
              variant={BannerVariant.Danger}
              Icon={<MissedCallIcon width={16} height={16} />}
              ctaBtnText="Mark as seen"
              className="call-logs-table-banner"
              ctaBtnClick={handleMarkCallAsSeen}
            />
          )
        }
      />
      <ConfirmationModalV3
        show={callLogToDelete !== null}
        title={
          <span className="delete-call-log-title">
            Delete call log for{' '}
            <span className="delete-call-log-prospect-text-truncate">
              {callLogToDelete?.prospectName}
            </span>
            ?
          </span>
        }
        className="delete-call-log-confirmation-modal"
        content="Once deleted, the call log will be removed from your list and will no longer be visible."
        onClose={handleCloseDeleteModal}
        onSubmit={handleConfirmDelete}
        submitButtonText="Yes"
        cancelButtonText="Cancel"
        showCloseIcon={false}
        iconType={ConfirmationModalIconType.TRASH_RED}
      />
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Deleting call logs is disabled in demo mode"
        content="This is a demo account with sample data. Signup to delete call logs"
      />
    </div>
  );
};

export default CallLogsTable;
